import { forwardRef, Inject, Pipe, PipeTransform } from "@angular/core";
import * as _ from 'lodash';
import { DictionaryItem, MetricRange } from "../../model";
import { BadgeSettings, StyleConfigurationService } from "../../service/style-configuration.service";
import { UnderscoreRemoverPipe } from "./underscore-remover.pipe";

@Pipe({ name: 'syBadge' })
export class ServitlyBadgePipe implements PipeTransform {

    constructor(
        @Inject(forwardRef(() => StyleConfigurationService)) private styleConfigurationService: StyleConfigurationService,
        @Inject(forwardRef(() => UnderscoreRemoverPipe)) private underscoreRemoverPipe: UnderscoreRemoverPipe
    ) { }

    transform(value: any, settings: BadgeSettings = {}): string {
        if (!value) {
            return '';
        }
        const mappingItem: DictionaryItem | MetricRange = this.getValueMapping(value, settings);
        if (mappingItem?.label) {
            value = mappingItem.label;
        }
        const colorHex = this.computeColor(String(value), settings, mappingItem);
        const leadingIcon = (settings.showLeadingIcon === false) ? '' : `<span style="color: ${colorHex}"></span>`; // must be explicit
        const normalizedLabel = this.underscoreRemoverPipe.transform(String(value));
        return `
            <div class="sy-badge" style="color: color-mix(in srgb, ${colorHex}, black 30%); background-color: color-mix(in srgb, ${colorHex} 15%, transparent)">
                ${leadingIcon}
                <div class="sy-badge-label">${normalizedLabel}</div>
            </div>
        `
    }

    private getValueMapping(value: any, settings: BadgeSettings): DictionaryItem | MetricRange {
        const model = (settings.metric || settings.property);
        const dictionary = model?.dictionary;
        if (dictionary?.length) {
            return dictionary.find(d => d.value == value);
        }
        const ranges = model?.ranges;
        if (ranges?.length) {
            let range = null;
            let from = -Infinity;
            for (let r of ranges) {
                if (value >= from && value <= r.to || _.isNil(r.to)) {
                    range = r;
                    break;
                }
                from = r.to;
            }
            if (range != null) {
                return range;
            }
        }
        return null;
    }


    private computeColor(value: string, settings: BadgeSettings, mappingItem: DictionaryItem | MetricRange): string {
        const uiToolkit = this.styleConfigurationService.getUIToolkit();
        if (!uiToolkit) {
            if (window['colorManager']) {
                return window['colorManager'].getOrRegisterColor(value);
            }
            return settings.color || "#000000";
        }
        let colorHex = null;
        if (mappingItem) {
            colorHex = uiToolkit.getColor(this.getColorFormMappingItem(mappingItem));
        }
        if (!colorHex && settings.colorTokenPrefix) {
            colorHex = uiToolkit.getColor(this.getTokenValue(value, settings.colorTokenPrefix));
        }
        if (!colorHex && settings.colorToken) {
            colorHex = uiToolkit.getColor(settings.colorToken)
        }
        if (!colorHex && settings.color) {
            return settings.color;
        }
        if (!colorHex) {
            colorHex = uiToolkit.getColor(this.getTokenValue(value)); // retrieve with suffix only
        }
        if (!colorHex) {
            colorHex = uiToolkit.assignColor(this.getTokenValue(value));
        }
        return colorHex;
    }

    private getColorFormMappingItem(mappingItem: DictionaryItem | MetricRange): string {
        if (mappingItem.colorToken) {
            return mappingItem.colorToken;
        }
        return 'severities-' + mappingItem.severity.toLowerCase();
    }

    private getTokenValue(label: string, colorTokenPrefix?: string): string {
        const tokenSuffix = (label as any).trim().replaceAll('_', '-').replaceAll(' ', '-').toLowerCase();
        if (colorTokenPrefix) {
            return `${colorTokenPrefix}-${tokenSuffix}`
        }
        return tokenSuffix;
    }
}

